/**
 * Map Filter Controls
 * Allows users to toggle marker categories on/off
 */

import L from 'leaflet';
import { MARKER_CATEGORIES, getMarkersByCategory } from './markers.js';
import buildMarkerPopup from './popup-builder.js';
import { createClusterGroup } from './clustering.js';

/**
 * Create category-based layer groups with clustering
 * @param {Object} iconsByCategory - Icon objects keyed by category
 * @param {Object} config - Map configuration
 * @returns {Object} Layer groups keyed by category
 */
export function createCategoryLayers(iconsByCategory, config) {
    const categoryLayers = {};

    Object.values(MARKER_CATEGORIES).forEach(category => {
        // Use cluster group instead of regular layer group
        const clusterGroup = createClusterGroup();
        const markers = getMarkersByCategory(category);

        markers.forEach(marker => {
            const leafletMarker = L.marker(marker.coordinates, {
                icon: iconsByCategory[category],
                alt: marker.info.title,
                title: marker.info.title,
                markerId: marker.id,
                markerCategory: category
            });

            const popupContent = buildMarkerPopup(marker.info);
            leafletMarker.bindPopup(popupContent, {
                minWidth: config.popupMinWidth,
                autoPan: config.popupAutoPan,
                closeButton: config.popupCloseButton,
                autoClose: config.popupAutoClose
            });

            // Add marker to cluster group
            clusterGroup.addLayer(leafletMarker);
        });

        categoryLayers[category] = clusterGroup;
    });

    return categoryLayers;
}

/**
 * Bind filter control UI to existing DOM elements
 * @param {L.Map} map - Leaflet map instance
 * @param {Object} categoryLayers - Layer groups by category
 */
export function createFilterControls(map, categoryLayers) {
    const container = document.querySelector('#map-controls');
    if (!container) return;

    const activeCategories = new Set();

    const updateMapLayers = () => {
        if (activeCategories.size === 0) {
            Object.values(categoryLayers).forEach(layer => {
                if (!map.hasLayer(layer)) map.addLayer(layer);
            });
        } else {
            Object.entries(categoryLayers).forEach(([category, layer]) => {
                if (activeCategories.has(category)) {
                    if (!map.hasLayer(layer)) map.addLayer(layer);
                } else {
                    if (map.hasLayer(layer)) map.removeLayer(layer);
                }
            });
        }
    };

    // Populate filter counts from layer data (query document to include mobile drawer buttons outside #map-controls)
    document.querySelectorAll('.filter-btn[data-category]').forEach(button => {
        const layer = categoryLayers[button.dataset.category];
        if (!layer) return;
        const countEl = button.querySelector('.filter-count');
        if (countEl) countEl.textContent = layer.getLayers().length;
    });

    // Listen for Alpine-dispatched filter events
    document.addEventListener('filter-toggle', (e) => {
        const { category, active } = e.detail;
        if (active) {
            activeCategories.add(category);
        } else {
            activeCategories.delete(category);
        }
        updateMapLayers();
    });

    document.addEventListener('filter-clear', () => {
        activeCategories.clear();
        updateMapLayers();
    });
}

export default { createCategoryLayers, createFilterControls };
